import { 
  Crown, Star, Users, Calendar, CreditCard, TrendingUp, Brain, Zap, 
  Settings, Globe, Package, Smartphone, Shield, BarChart3, Database, 
  CheckCircle, Lock, Clock
} from 'lucide-react';
import { ProductConfigs } from './types';

export const boulevardPremiumEnhancedConfigs: ProductConfigs = {
  'boulevard-premium-platform': {
    keyBenefits: [
      {
        icon: Crown,
        title: 'Premium Client Management',
        description: 'Luxury-focused client relationship management with VIP services, premium booking experiences, and white-glove customer care.',
        metrics: '95% premium client satisfaction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Elite Appointment Scheduling',
        description: 'Sophisticated scheduling system with priority booking, concierge services, and personalized appointment coordination.',
        metrics: '99% appointment satisfaction rate',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Star,
        title: 'Luxury Service Experience',
        description: 'Premium service delivery platform with bespoke treatments, personalized preferences, and luxury amenity management.',
        metrics: '4.9/5 luxury experience rating',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'VIP Customer Profiles',
        description: 'Comprehensive premium client profiles with detailed preferences, service history, and personalized care protocols.',
        metrics: '90% client retention in premium tier',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: CreditCard,
        title: 'Premium Payment Solutions',
        description: 'Luxury payment processing with concierge billing, membership management, and exclusive payment options.',
        metrics: '99.9% premium payment success',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimization',
        description: 'Advanced revenue management with premium pricing strategies, upselling tools, and profit maximization analytics.',
        metrics: '80% revenue increase average',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Luxury Intelligence',
        description: 'Machine learning algorithms optimizing premium client experiences, service recommendations, and luxury service delivery.',
        metrics: 'Predictive luxury accuracy of 94%',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Premium Automation Suite',
        description: 'Automated luxury service workflows with personalized communications, VIP alerts, and premium service triggers.',
        metrics: '300% efficiency in premium operations',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Luxury Workflows',
        description: 'Flexible luxury service configuration with bespoke protocols, premium service standards, and elite operational procedures.',
        metrics: 'Unlimited luxury customisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Premium Management',
        description: 'Centralized luxury operations across multiple premium locations with consistent high-end service standards.',
        metrics: 'Manage luxury operations globally',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Package,
        title: 'Premium Inventory Management',
        description: 'Luxury product and service inventory with premium brand management, exclusive product tracking, and VIP availability.',
        metrics: '95% premium product availability',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Smartphone,
        title: 'Luxury Mobile Experience',
        description: 'Premium mobile platform with concierge-level service access, VIP booking, and luxury service management.',
        metrics: '4.9/5 premium mobile satisfaction',
        color: 'from-teal-500 to-green-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};